import React, { useEffect, useMemo, useState } from 'react';
import { formatMinutesToHM } from './format';
import { deleteTimeEntry, updateTimeEntry } from '../api';

function fmtYmd(v) {
  if (!v) return '—';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s;
}

function safeLabel(v, fallback = '—') {
  const s = (v == null) ? '' : String(v).trim();
  return s ? s : fallback;
}

export default function TimeEntryDetailsModal({ open, onClose, timeEntryId, fetcher, viewer = null, onChanged = null }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);

  const [editing, setEditing] = useState(false);
  const [minutesInput, setMinutesInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [overrideReasonInput, setOverrideReasonInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionErr, setActionErr] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!open || !timeEntryId) {
        setLoading(false);
        setErr('');
        setData(null);
        return;
      }
      if (typeof fetcher !== 'function') {
        setErr('Missing fetcher');
        return;
      }

      setLoading(true);
      setErr('');
      try {
        const d = await fetcher(timeEntryId);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'Failed to load time entry');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [open, timeEntryId, fetcher]);

  useEffect(() => {
    if (!open) return;
    if (!data) return;
    setEditing(false);
    setActionErr('');
    setMinutesInput(String(Number(data?.minutes || 0) || 0));
    setDescriptionInput(String(data?.description || ''));
    setOverrideReasonInput(String(data?.override_reason || ''));
  }, [open, data?.id]);

  const title = useMemo(() => {
    const who = safeLabel(data?.user?.display_name, data?.user?.email ? String(data.user.email) : 'Time entry');
    const date = safeLabel(data?.entry_date, '');
    return date ? `${who} • ${date}` : who;
  }, [data]);

  if (!open) return null;

  const minutes = Number(data?.minutes || 0) || 0;
  const source = safeLabel(data?.source, data?.is_system ? 'SYSTEM' : '—');

  const viewerRole = String(viewer?.role || '').trim().toLowerCase();
  const viewerName = String(viewer?.display_name || '').trim().toLowerCase();
  const viewerId = viewer?.id != null ? String(viewer.id) : null;
  const entryUserId = data?.user?.id != null ? String(data.user.id) : (data?.user_id != null ? String(data.user_id) : null);
  const isOwner = !!(viewerId && entryUserId && viewerId === entryUserId);
  const isAdminLike = viewerRole === 'admin' || viewerName === 'admin';
  const isManagerOrAdmin = viewerRole === 'manager' || viewerRole === 'admin';
  const isAutoFinalize = !!data?.is_system && String(data?.source || '') === 'AUTO_FINALIZE';

  // UX gating mirrors backend rule: manager/admin can override locked AUTO_FINALIZE entries;
  // employees cannot.
  const canOverrideLocked = isAutoFinalize && isManagerOrAdmin;
  const canEditAutoFinalize = isAutoFinalize && isManagerOrAdmin && (!data?.locked || canOverrideLocked);
  const canDeleteAutoFinalize = canEditAutoFinalize;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[6px]" onClick={() => onClose && onClose()} />

      <div className="absolute right-1/2 translate-x-1/2 top-16 w-[720px] max-w-[96vw] bg-white dark:bg-gray-900 rounded shadow-lg border border-gray-200 dark:border-gray-800 p-4 max-h-[82vh] overflow-auto">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">Time entry details</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 truncate">{title}</div>
          </div>
          <div className="flex items-center gap-2">
            {canEditAutoFinalize && !editing && (
              <button
                type="button"
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 text-xs font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => setEditing(true)}
              >Edit</button>
            )}
            {canDeleteAutoFinalize && (
              <button
                type="button"
                className="px-3 py-1.5 rounded border border-red-300 dark:border-red-900/50 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20"
                disabled={saving}
                onClick={async () => {
                  setActionErr('');
                  if (!data?.id) return;
                  const ok = window.confirm('Delete this auto-finalized time entry?');
                  if (!ok) return;
                  try {
                    setSaving(true);
                    await deleteTimeEntry(data.id);
                    if (typeof onChanged === 'function') onChanged({ type: 'deleted', id: data.id });
                    if (onClose) onClose();
                  } catch (e) {
                    setActionErr(e?.message || 'Failed to delete time entry');
                  } finally {
                    setSaving(false);
                  }
                }}
              >Delete</button>
            )}
            <button onClick={() => onClose && onClose()} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-gray-700 dark:text-gray-300">Loading…</div>
        ) : err ? (
          <div className="text-sm text-red-600">{err}</div>
        ) : (
          <div className="space-y-3">
            {actionErr && <div className="text-sm text-red-600">{actionErr}</div>}

            {editing && (
              <div className="rounded border border-gray-200 dark:border-gray-800 p-3 space-y-2">
                <div className="text-sm font-medium">Edit auto-finalized entry</div>

                <label className="block text-sm">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">Minutes</div>
                  <input
                    type="number"
                    min={1}
                    max={1440}
                    className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1"
                    value={minutesInput}
                    onChange={(e) => setMinutesInput(e.target.value)}
                    disabled={saving}
                  />
                </label>

                <label className="block text-sm">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">Notes</div>
                  <textarea
                    className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1"
                    rows={2}
                    value={descriptionInput}
                    onChange={(e) => setDescriptionInput(e.target.value)}
                    disabled={saving}
                  />
                </label>

                <label className="block text-sm">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">Override reason (optional)</div>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1"
                    value={overrideReasonInput}
                    onChange={(e) => setOverrideReasonInput(e.target.value)}
                    disabled={saving}
                  />
                </label>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 text-xs font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => { setEditing(false); setActionErr(''); }}
                    disabled={saving}
                  >Cancel</button>
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium disabled:opacity-50"
                    disabled={saving}
                    onClick={async () => {
                      setActionErr('');
                      if (!data?.id) return;
                      const minutesNum = Number(minutesInput);
                      if (!Number.isInteger(minutesNum) || minutesNum <= 0 || minutesNum > 1440) {
                        setActionErr('Minutes must be an integer between 1 and 1440');
                        return;
                      }
                      try {
                        setSaving(true);
                        await updateTimeEntry(data.id, {
                          minutes: minutesNum,
                          description: descriptionInput,
                          override_reason: overrideReasonInput || null,
                        });
                        const refreshed = await fetcher(data.id);
                        setData(refreshed);
                        setEditing(false);
                        if (typeof onChanged === 'function') onChanged({ type: 'updated', id: data.id });
                      } catch (e) {
                        setActionErr(e?.message || 'Failed to update time entry');
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >Save</button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Person</div>
              <div className="font-medium">{safeLabel(data?.user?.display_name, data?.user?.email ? String(data.user.email) : '—')}</div>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Date</div>
              <div className="font-medium">{fmtYmd(data?.entry_date)}</div>
            </div>

            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Project</div>
              <div className="font-medium">{safeLabel(data?.project?.name, data?.project_id ? `#${data.project_id}` : '—')}</div>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Job / Task</div>
              <div className="font-medium">
                {safeLabel(data?.job?.name, data?.job_id ? `Job #${data.job_id}` : '—')}
                {data?.task?.name ? ` › ${data.task.name}` : ''}
              </div>
            </div>

            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Minutes</div>
              <div className="font-medium tabular-nums">{formatMinutesToHM(minutes)}</div>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Source</div>
              <div className="font-medium">{source}</div>
            </div>

            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Flags</div>
              <div className="font-medium">
                {data?.is_system ? 'System' : 'Manual'}
                {data?.locked ? ' • Locked' : ''}
                {data?.is_other ? ' • Other' : ''}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Assignment</div>
              <div className="font-medium">{data?.assignment_id ? String(data.assignment_id) : '—'}</div>
            </div>

            <div className="col-span-2">
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Notes</div>
              <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{safeLabel(data?.description, '—')}</div>
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
